'use client'

import { RefObject } from 'react'
import { A11y, Pagination } from 'swiper/modules'
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/a11y'
import 'swiper/css/pagination'
import { AtButton, NavArrowLeft, NavArrowRight } from '../../atoms'
import { AtButtonDimensions, AtButtonTypes } from '../../types'
import { Overlay } from './overlay'
import { Media, OrMedia } from '../../organisms'
import { buttonPositioning } from './configuration'
import type { AspectRatio, Image } from './'

export type SwiperEngineProps = {
  swiperRef: RefObject<SwiperRef | null>
  media: Media[]
  images?: Image[]
  href?: string
  lazy: boolean
  aspectRatio: AspectRatio
  className: string
  beginning: boolean
  end: boolean
  onBeginningChange: (beginning: boolean) => void
  onEndChange: (end: boolean) => void
  onSlideChange?: (index: number) => void
  onClick?: (index: number) => void
}

// CSS fallback so the layout still reserves the right space if width/height
// attributes are ever missing (e.g. an asset without Sanity metadata).
const fallbackAspectRatio = (image?: Image) =>
  image?.width && image?.height ? `${image.width} / ${image.height}` : undefined

export const SwiperEngine = ({
  swiperRef,
  media,
  images,
  href,
  lazy,
  aspectRatio,
  className,
  beginning,
  end,
  onBeginningChange,
  onEndChange,
  onSlideChange,
  onClick,
}: SwiperEngineProps) => (
  <Swiper
    modules={[A11y, Pagination]}
    wrapperTag={href ? 'a' : undefined}
    onBeforeInit={(swiper) => {
      if (href) {
        const wrapper = swiper.wrapperEl as HTMLAnchorElement
        wrapper.href = href
        wrapper.tabIndex = -1 // Don't allow tab focusing since this anchor should be used inside a card (which should hold the focus)
      }
    }}
    pagination={{
      dynamicBullets: true,
      dynamicMainBullets: 5,
    }}
    onSlideChange={(swiper) => {
      onEndChange(swiper.isEnd)
      onBeginningChange(swiper.isBeginning)
      onSlideChange?.(swiper.activeIndex)
    }}
    onClick={(swiper) => (onClick ? onClick(swiper.activeIndex) : 0)}
    ref={swiperRef}
    className={`${className} group`}
  >
    <div className={`${buttonPositioning} left-4`}>
      <AtButton
        ariaLabel="Previous slide"
        className="w-10"
        type={AtButtonTypes.PRIMARY}
        size={AtButtonDimensions.SMALL}
        isDisabled={beginning}
        onClick={() => swiperRef.current?.swiper.slidePrev()}
        IconLeftRef={NavArrowLeft}
      />
    </div>
    <div className={`${buttonPositioning} left-full -translate-x-[calc(100%+1rem)]`}>
      <AtButton
        ariaLabel="Next slide"
        className="w-10"
        type={AtButtonTypes.PRIMARY}
        size={AtButtonDimensions.SMALL}
        isDisabled={end}
        onClick={() => swiperRef.current?.swiper.slideNext()}
        IconLeftRef={NavArrowRight}
      />
    </div>
    <Overlay className="absolute z-[5] bottom-[0] left-1/2 -translate-x-1/2 pointer-events-none" />
    {media.length
      ? media.map((media, index) => (
          <SwiperSlide key={index}>
            <OrMedia {...media} />
          </SwiperSlide>
        ))
      : images?.map((image, index) => (
          <SwiperSlide key={index}>
            <img
              alt={image.alt || 'image'}
              src={image.src}
              srcSet={image.srcset}
              sizes={image.sizes}
              width={image.width}
              height={image.height}
              style={aspectRatio === 'custom' ? { aspectRatio: fallbackAspectRatio(image) } : undefined}
              fetchPriority={lazy || index !== 0 ? 'auto' : 'high'}
              loading={lazy || index !== 0 ? 'lazy' : 'eager'}
              className="object-cover w-full h-full"
            />
          </SwiperSlide>
        ))}
  </Swiper>
)
